import React from 'react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  styled,
  Badge,
  Divider,
  Snackbar,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import DashboardIcon from '@mui/icons-material/Dashboard';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import PersonIcon from '@mui/icons-material/Person';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import FeedbackIcon from '@mui/icons-material/Feedback';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth } from '../firebase';
import { signOut } from 'firebase/auth';
import { getDoc, doc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { User } from 'firebase/auth';
import { onAuthStateChanged } from 'firebase/auth';

const DRAWER_WIDTH = 280;

const SidebarContainer = styled(Drawer)({
  width: DRAWER_WIDTH,
  flexShrink: 0,
  '& .MuiDrawer-paper': {
    width: DRAWER_WIDTH,
    boxSizing: 'border-box',
    backgroundColor: '#ffffff',
    borderRight: '1px solid rgba(0, 0, 0, 0.12)',
    display: 'flex',
    flexDirection: 'column',
    willChange: 'transform',
    '@media (max-width: 1024px)': {
      width: '260px'
    }
  },
});

const LogoContainer = styled(Box)({
  padding: '0',
  borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  backgroundColor: '#ffffff',
  willChange: 'transform'
});

const LogoImage = styled('img')({
  width: 'auto',
  height: '100px',
  objectFit: 'contain',
  willChange: 'transform',
  padding: '0 16px'
});

const MenuList = styled(List)({
  padding: '16px',
  flex: 1,
  '@media (max-width: 1024px)': {
    padding: '12px'
  }
});

interface StyledListItemProps {
  active?: boolean;
}

const StyledListItem = styled(ListItem, {
  shouldForwardProp: (prop) => prop !== 'active',
})<StyledListItemProps>(({ active }) => ({
  padding: '8px 12px',
  borderRadius: '8px',
  marginBottom: '4px',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: 'rgba(255, 112, 67, 0.08)'
  },
  ...(active && {
    backgroundColor: 'rgba(255, 112, 67, 0.12)',
    '& .MuiListItemIcon-root': {
      color: '#FF7043'
    },
    '& .MuiListItemText-root': {
      '& .MuiTypography-root': {
        color: '#FF7043',
        fontWeight: 600
      }
    }
  }),
  '@media (max-width: 1024px)': {
    padding: '12px 16px',
    marginBottom: '6px',
    '& .MuiListItemIcon-root': {
      minWidth: '40px'
    },
    '& .MuiListItemText-root .MuiTypography-root': {
      fontSize: '1rem'
    }
  }
}));

const ProfileContainer = styled(Box)({
  padding: '16px',
  borderTop: '1px solid rgba(0, 0, 0, 0.12)',
  '@media (max-width: 1024px)': {
    padding: '12px'
  }
});

const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: '#44b700',
    color: '#44b700',
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    '&::after': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      borderRadius: '50%',
      border: '1px solid currentColor',
      content: '""',
    },
  },
}));

const ProfileItem = styled(StyledListItem)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '12px 16px',
  '& .MuiListItemIcon-root': {
    minWidth: 'auto',
  },
  '& .MuiListItemText-root': {
    margin: 0,
  },
  '@media (max-width: 1024px)': {
    padding: '14px 16px',
    gap: '14px'
  }
});

const IconCircle = styled(Box)({
  width: 32,
  height: 32,
  borderRadius: '50%',
  backgroundColor: '#FF7043',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '@media (max-width: 1024px)': {
    width: 36,
    height: 36,
    '& .MuiSvgIcon-root': {
      fontSize: '22px'
    }
  }
});

const LogoutButton = styled(ListItem)({
  padding: '8px 12px',
  borderRadius: '8px',
  marginTop: 'auto',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: 'rgba(244, 67, 54, 0.08)'
  },
  '@media (max-width: 1024px)': {
    padding: '12px 16px',
    '& .MuiListItemIcon-root': {
      minWidth: '40px'
    },
    '& .MuiListItemText-root .MuiTypography-root': {
      fontSize: '1rem'
    }
  }
});

interface Track {
  id: string;
  name: string;
  is_active: boolean;
  price: number;
  is_free: boolean;
  audio_url?: string;
  created_at?: string;
  updated_at?: string;
}

interface MenuItem {
  id: string;
  text: string;
  icon: React.ReactNode;
  path: string;
}

const menuItems: MenuItem[] = [
  { id: 'dashboard', text: 'Dashboard', icon: <HomeIcon />, path: '/dashboard' },
  { id: 'yantra', text: 'Yantra Card Management', icon: <CardGiftcardIcon />, path: '/yantra-cards' },
  { id: 'tracks', text: 'Track Management', icon: <MusicNoteIcon />, path: '/manage-tracks' },
  { id: 'feedback', text: 'User Feedback', icon: <FeedbackIcon />, path: '/user-feedback' }
];

export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeItem, setActiveItem] = React.useState('dashboard');
  const [userEmail, setUserEmail] = React.useState('');
  const [userName, setUserName] = React.useState('');
  const [errors, setErrors] = React.useState({
    trackName: '',
    price: ''
  });
  const [snackbar, setSnackbar] = React.useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });
  const [tracks, setTracks] = React.useState<Track[]>([]);
  const [logoutDialogOpen, setLogoutDialogOpen] = React.useState(false);

  React.useEffect(() => {
    // Set active item based on current path
    const path = location.pathname;
    if (path === '/dashboard' || path === '/') {
      setActiveItem('dashboard');
    } else if (path === '/yantra-cards') {
      setActiveItem('yantra');
    } else if (path === '/manage-tracks') {
      setActiveItem('tracks');
    } else if (path === '/user-feedback') {
      setActiveItem('feedback');
    }
  }, [location.pathname]);

  const fetchAdminUserData = async (user: User) => {
    try {
      const adminUsersRef = collection(db, 'admin_users');
      const q = query(adminUsersRef, where('email', '==', user.email));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const adminUserData = querySnapshot.docs[0].data();
        setUserName(adminUserData.first_name || 'Unknown User');
      } else {
        setUserName('Unknown User');
      }
    } catch (error) {
      console.error('Error fetching admin user data:', error);
      setUserName('Unknown User');
    }
  };

  React.useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setUserEmail(user.email || '');
        await fetchAdminUserData(user);
      }
    });

    return () => unsubscribe();
  }, []);

  const handleNavigation = (path: string, id: string) => {
    setActiveItem(id);
    navigate(path);
  };

  const handleLogout = () => {
    setLogoutDialogOpen(true);
  };

  const handleLogoutConfirm = async () => {
    try {
      await signOut(auth);
      setSnackbar({ open: true, message: 'Logged out successfully', severity: 'success' });
      // Short delay to show the success message before redirecting
      setTimeout(() => {
        navigate('/', { replace: true });
      }, 1000);
    } catch (error) {
      console.error('Error signing out:', error);
      setSnackbar({ open: true, message: 'Error logging out. Please try again.', severity: 'error' });
    } finally {
      setLogoutDialogOpen(false);
    }
  };

  const handleLogoutCancel = () => {
    setLogoutDialogOpen(false);
  };

  return (
    <>
      <SidebarContainer variant="permanent" anchor="left">
        <LogoContainer>
          <LogoImage 
            src="/yantras-logo.png" 
            alt="Yantra Meditation Logo"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.src = '/yantras-logo.png'; // Fallback image
            }}
          />
        </LogoContainer>
        
        <MenuList>
          {menuItems.map((item) => (
            <StyledListItem
              key={item.id}
              active={activeItem === item.id}
              onClick={() => handleNavigation(item.path, item.id)}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.text}
                primaryTypographyProps={{
                  fontSize: '0.875rem',
                  fontWeight: activeItem === item.id ? 500 : 400
                }}
              />
            </StyledListItem>
          ))}
        </MenuList>

        <ProfileContainer>
          <ProfileItem>
            <StyledBadge
              overlap="circular"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              variant="dot"
            >
              <IconCircle>
                <PersonIcon sx={{ fontSize: 20, color: '#FFFFFF' }} />
              </IconCircle>
            </StyledBadge>
            <Box sx={{ flex: 1 }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#424242', 
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {userName || 'User'}
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: '#757575',
                  fontSize: '0.75rem',
                  display: 'block',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {userEmail}
              </Typography>
            </Box>
          </ProfileItem>
          <Divider sx={{ my: 1 }} />
          <LogoutButton onClick={handleLogout}>
            <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText 
              primary="Logout" 
              primaryTypographyProps={{
                fontSize: '0.875rem',
              }}
            />
          </LogoutButton>
        </ProfileContainer>
      </SidebarContainer>

      {/* Logout Confirmation Dialog */}
      <Dialog
        open={logoutDialogOpen}
        onClose={handleLogoutCancel}
        PaperProps={{
          sx: {
            borderRadius: '8px',
            padding: '16px',
            minWidth: '300px'
          }
        }}
      >
        <DialogTitle sx={{ 
          fontSize: '18px', 
          fontWeight: 'bold',
          color: '#424242',
          textAlign: 'center'
        }}>
          Confirm Logout
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center' }}>
          <Typography sx={{ color: '#424242' }}>
            Are you sure you want to logout?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ 
          justifyContent: 'center',
          gap: 2,
          padding: '16px'
        }}>
          <Button
            onClick={handleLogoutCancel}
            variant="outlined"
            sx={{
              color: '#424242',
              borderColor: '#424242',
              '&:hover': {
                borderColor: '#424242',
                backgroundColor: 'rgba(66, 66, 66, 0.04)'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleLogoutConfirm}
            variant="contained"
            sx={{
              backgroundColor: '#FF7043',
              color: '#ffffff',
              '&:hover': {
                backgroundColor: '#F4511E'
              }
            }}
          >
            Logout
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
} 